import { useEffect, useMemo, useRef, useState } from "react";
import api from "../../api/axios";
import { InvoicePositionAdjustmentPercent } from "../../types/invoice";
import { ymdToDate } from "../../utils/date";
import { colors, spacing, borderRadius, typography, styles, createButtonStyle, shadows } from "../../styles/theme";

type AllegroClient = { id: string; nickname: string };
type GoogleAdsClient = {
  id: number;
  customer_id: string;
  descriptive_name: string;
  mcc_label: string;
  currency_code: string;
};
type Client = AllegroClient | GoogleAdsClient;
type SourceType = 'allegro' | 'google_ads';
type MissingDaysResponse = string[] | { missing: string[] };

type Props = {
  onAdd: (pos: InvoicePositionAdjustmentPercent) => void;
  onCancel: () => void;
};

export default function AdjustmentPercentForm({ onAdd, onCancel }: Props) {
  // Wspólne
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [vat, setVat] = useState<number>(23);
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [nameDirty, setNameDirty] = useState(false);

  // Pola procentowe
  const [sourceType, setSourceType] = useState<SourceType>('allegro');
  const [clientId, setClientId] = useState("");  // nickname (Allegro) or customer_id (Google) - for invoice_preview
  const [clientNumericId, setClientNumericId] = useState("");  // numeric DB id (Allegro only) - for missing-days
  const [clientSearch, setClientSearch] = useState("");
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [percent, setPercent] = useState<number>(10);
  const [threshold, setThreshold] = useState<number>(1000);
  const [capEnabled, setCapEnabled] = useState(false);
  const [capAmount, setCapAmount] = useState<number>(0);
  const [gtuCode, setGtuCode] = useState<string>("GTU_12");
  const [missingDays, setMissingDays] = useState<string[]>([]);
  const [loadingMissing, setLoadingMissing] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inFlight = useRef<AbortController | null>(null);

  // Auto-generuj nazwę
  const defaultName = useMemo(
    () => threshold === 0
      ? `${percent}% od całego budżetu (wyrównanie)`
      : `${percent}% od nadwyżki powyżej ${Math.round(threshold)} PLN (wyrównanie)`,
    [percent, threshold]
  );

  useEffect(() => {
    if (!nameDirty) {
      setName(defaultName);
    }
  }, [defaultName, nameDirty]);

  // Click outside - zamknij dropdown klientów
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setClients([]);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Live search klientów (Allegro / Google Ads)
  const handleClientSearch = async (value: string) => {
    setClientSearch(value);
    setClientId("");
    setClientNumericId("");
    setError("");
    setWarning("");
    setMissingDays([]);

    if (value.length < 2) {
      setClients([]);
      return;
    }

    try {
      setLoadingClients(true);
      let resp;
      if (sourceType === 'allegro') {
        resp = await api.get("/allegro-clients", { params: { nickname: value } });
      } else {
        resp = await api.get("/google-ads/customers", { params: { search: value } });
      }
      setClients(Array.isArray(resp.data) ? resp.data : []);
    } catch {
      setClients([]);
      setError(`Błąd wyszukiwania klientów ${sourceType === 'allegro' ? 'Allegro' : 'Google Ads'}.`);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleSelectClient = (client: Client) => {
    if (sourceType === 'allegro') {
      const allegroClient = client as AllegroClient;
      setClientSearch(allegroClient.nickname);
      setClientId(allegroClient.nickname);  // Backend invoice_preview expects nickname
      setClientNumericId(String(allegroClient.id));  // missing-days expects numeric DB id
    } else {
      const googleClient = client as GoogleAdsClient;
      setClientSearch(googleClient.descriptive_name || googleClient.customer_id);
      setClientId(googleClient.customer_id);
      setClientNumericId("");  // Not needed for Google Ads
    }

    setClients([]);
    setError("");
    setWarning("");
  };

  // Missing days check
  useEffect(() => {
    if (!clientId || !dateFrom || !dateTo) { setMissingDays([]); return; }
    // For Allegro, also need clientNumericId for the missing-days endpoint
    if (sourceType === 'allegro' && !clientNumericId) { setMissingDays([]); return; }
    setError("");
    setWarning("");

    const run = async () => {
      try {
        inFlight.current?.abort();
        const ctrl = new AbortController();
        inFlight.current = ctrl;

        setLoadingMissing(true);

        let resp;
        if (sourceType === 'allegro') {
          // Allegro missing-days expects numeric DB id
          resp = await api.get<MissingDaysResponse>("/missing-days", {
            params: { client_id: clientNumericId, date_from: dateFrom, date_to: dateTo },
            signal: ctrl.signal,
          });
        } else {
          // Google Ads missing-days expects customer_id
          resp = await api.get<MissingDaysResponse>("/google-ads/missing-days", {
            params: { customer_id: clientId, date_from: dateFrom, date_to: dateTo },
            signal: ctrl.signal,
          });
        }

        const payload = resp.data;
        const days = Array.isArray(payload) ? payload : (payload.missing ?? []);
        setMissingDays(days);

        if (days.length) {
          const head = days.slice(0, 6).join(", ");
          const tail = days.length > 6 ? ` ... (+${days.length - 6})` : "";
          setWarning(`Uwaga: w tym okresie brakuje danych/kosztów dla dni: ${head}${tail}`);
        } else {
          setWarning("");
        }
      } catch (err: any) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        const errMsg = err?.response?.data?.detail || err?.message || "Nieznany błąd";
        setError(`Błąd podczas sprawdzania brakujących dni (${sourceType}): ${errMsg}`);
        setMissingDays([]);
        setWarning("");
      } finally {
        setLoadingMissing(false);
      }
    };

    const t = setTimeout(run, 300);
    return () => clearTimeout(t);
  }, [clientId, clientNumericId, dateFrom, dateTo, sourceType]);

  // Walidacja
  const validDates = dateFrom && dateTo && ymdToDate(dateFrom) <= ymdToDate(dateTo);
  const disabled = !(validDates && clientId && percent > 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;

    onAdd({
      type: "adjustment_percent",
      name,
      date_from: dateFrom,
      date_to: dateTo,
      vat,
      description,
      source_type: sourceType,
      client_id: clientId,
      client_numeric_id: clientNumericId || undefined,
      percent,
      threshold,
      cap_enabled: capEnabled,
      cap_amount: capEnabled ? capAmount : undefined,
      gtu_code: gtuCode || null,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        padding: spacing.lg,
        background: colors.backgroundAlt,
        borderRadius: borderRadius.md,
        border: `1px solid ${colors.border}`,
      }}
    >
      <div style={{
        marginBottom: spacing.md,
        padding: spacing.md,
        background: colors.warningLight,
        borderLeft: `3px solid ${colors.warning}`,
        borderRadius: borderRadius.sm,
      }}>
        <strong style={{ fontSize: typography.fontSize.base }}>Pozycja: Wyrównanie %</strong>
        <div style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, marginTop: spacing.xs }}>
          Wyrównanie obliczone jako procent od budżetu klienta
        </div>
      </div>

      {/* Nazwa + Data od/do */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: spacing.md, marginBottom: spacing.md }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Nazwa pozycji:
          </span>
          <input
            value={name}
            onChange={e => { setName(e.target.value); setNameDirty(true); }}
            placeholder={defaultName}
            style={styles.input}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Data od:
          </span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={styles.input}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Data do:
          </span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={styles.input}
          />
        </label>
      </div>

      {/* VAT + GTU + Opis */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 3fr', gap: spacing.md, marginBottom: spacing.md }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            VAT (%):
          </span>
          <select value={vat} onChange={(e) => setVat(Number(e.target.value))} style={styles.select}>
            <option value={23}>23%</option>
            <option value={8}>8%</option>
            <option value={0}>0%</option>
            <option value={5}>5%</option>
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            GTU:
          </span>
          <select value={gtuCode} onChange={(e) => setGtuCode(e.target.value)} style={styles.select}>
            <option value="GTU_12">GTU_12</option>
            <option value="">Brak</option>
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Opis (opcjonalny):
          </span>
          <input
            placeholder="Tylko wewnętrznie"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={styles.input}
          />
        </label>
      </div>

      {/* Source Selector */}
      <div style={{ marginBottom: spacing.md }}>
        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
            Źródło danych:
          </span>
          <select
            value={sourceType}
            onChange={e => {
              setSourceType(e.target.value as SourceType);
              setClientSearch("");
              setClientId("");
              setClientNumericId("");
              setClients([]);
              setError("");
              setWarning("");
              setMissingDays([]);
            }}
            style={styles.select}
          >
            <option value="allegro">Allegro Ads</option>
            <option value="google_ads">Google Ads</option>
          </select>
        </label>
      </div>

      {/* Client Search */}
      <div ref={wrapperRef} style={{ position: "relative", marginBottom: spacing.md }}>
        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Klient {sourceType === 'allegro' ? 'Allegro' : 'Google Ads'}:
          </span>
          <input
            value={clientSearch}
            onChange={e => handleClientSearch(e.target.value)}
            placeholder={sourceType === 'allegro' ? "Wpisz nick (min 2 znaki)" : "Wpisz nazwę klienta (min 2 znaki)"}
            autoComplete="off"
            style={{ ...styles.input, width: "100%" }}
          />
        </label>
        {loadingClients && (
          <div style={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, marginTop: spacing.xs }}>
            Szukam...
          </div>
        )}
        {clients.length > 0 && (
          <ul style={{
            position: "absolute", top: "100%", left: 0, background: colors.background,
            border: `1px solid ${colors.border}`, zIndex: 1000, listStyle: "none",
            margin: `${spacing.xs}px 0 0 0`, padding: 0, width: "100%", maxHeight: 180, overflowY: "auto",
            boxShadow: shadows.lg, borderRadius: borderRadius.sm
          }}>
            {clients.map(c => {
              const isAllegro = sourceType === 'allegro';
              const displayText = isAllegro
                ? `${(c as AllegroClient).nickname} (${c.id})`
                : `${(c as GoogleAdsClient).descriptive_name || (c as GoogleAdsClient).customer_id} (${(c as GoogleAdsClient).customer_id})`;

              return (
                <li key={isAllegro ? c.id : (c as GoogleAdsClient).customer_id}
                    style={{
                      padding: spacing.md,
                      cursor: "pointer",
                      borderBottom: `1px solid ${colors.borderLight}`,
                      transition: "background 0.2s ease"
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = colors.primaryLight; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    onClick={() => handleSelectClient(c)}>
                  {displayText}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Procent + Próg */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing.md, marginBottom: spacing.md }}>
        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Procent (%):
          </span>
          <input
            type="number"
            step="0.1"
            value={percent}
            min={0}
            max={100}
            onChange={e => setPercent(Number(e.target.value))}
            style={styles.input}
          />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Próg (PLN):
          </span>
          <input
            type="number"
            step="0.01"
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
            style={styles.input}
          />
        </label>
      </div>

      {/* Cap (limit nadwyżki) */}
      <div style={{
        marginBottom: spacing.md,
        padding: spacing.md,
        background: colors.gray50,
        borderRadius: borderRadius.sm,
        border: `1px solid ${colors.border}`
      }}>
        <label style={{ display: "flex", alignItems: "center", gap: spacing.sm, cursor: "pointer", marginBottom: capEnabled ? spacing.md : 0 }}>
          <input
            type="checkbox"
            checked={capEnabled}
            onChange={e => setCapEnabled(e.target.checked)}
            style={{ cursor: "pointer" }}
          />
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Blokada nadwyżki (cap)
          </span>
        </label>
        {capEnabled && (
          <div style={{ marginTop: spacing.sm }}>
            <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
              <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                Maksymalna kwota netto (PLN):
              </span>
              <input
                type="number"
                step="0.01"
                min={0}
                value={capAmount}
                onChange={e => setCapAmount(Number(e.target.value))}
                placeholder="Wpisz maksymalną kwotę"
                style={styles.input}
              />
            </label>
            <div style={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, marginTop: spacing.xs }}>
              Jeśli obliczona kwota przekroczy limit, zostanie obcięta do tej wartości.
            </div>
          </div>
        )}
      </div>

      {/* Missing days warning */}
      {missingDays.length > 0 && (
        <div style={{
          marginBottom: spacing.md,
          padding: spacing.md,
          background: colors.warningLight,
          borderLeft: `3px solid ${colors.warning}`,
          borderRadius: borderRadius.sm,
        }}>
          <strong>Uwaga: Brak danych dla {missingDays.length} dni</strong>
          <div style={{ fontSize: typography.fontSize.sm, marginTop: spacing.xs }}>
            {missingDays.slice(0, 6).join(", ")}
            {missingDays.length > 6 && ` i ${missingDays.length - 6} więcej...`}
          </div>
          <div style={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, marginTop: spacing.xs }}>
            Możesz kontynuować mimo brakujących danych.
          </div>
        </div>
      )}

      {loadingMissing && (
        <div style={{
          padding: spacing.sm,
          background: colors.infoLight,
          borderRadius: borderRadius.sm,
          color: colors.infoDark,
          marginBottom: spacing.md,
          fontSize: typography.fontSize.sm
        }}>
          Sprawdzam brakujące dni...
        </div>
      )}

      {/* Error/Warning messages */}
      {warning && (
        <div style={{
          padding: spacing.md,
          background: colors.warningLight,
          border: `1px solid ${colors.warning}`,
          borderRadius: borderRadius.sm,
          color: colors.warningDark,
          marginBottom: spacing.md,
          fontSize: typography.fontSize.sm
        }}>
          {warning}
        </div>
      )}

      {error && (
        <div style={{
          padding: spacing.md,
          background: colors.errorLight,
          border: `1px solid ${colors.error}`,
          borderRadius: borderRadius.sm,
          color: colors.errorDark,
          marginBottom: spacing.md,
          fontSize: typography.fontSize.sm
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: spacing.md }}>
        <button type="submit" disabled={disabled} style={createButtonStyle('primary', disabled)}>
          Dodaj pozycję
        </button>
        <button type="button" onClick={onCancel} style={createButtonStyle('secondary')}>
          Anuluj
        </button>
      </div>
    </form>
  );
}
